import { FC, useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector, useCreateCsv } from '../../hooks'
import { formatDate } from 'src/utils/helpers'
import SessionParticipants from './SessionParticipants'
import CSVButton from '../CSVButton'
import EllipsesDropdown from '../EllipsesDropdown'
import BackButton from '../Navigation/BackButton'
import TagDropdown from '../Dropdown/TagDropdown'
import { setType } from 'src/app/modal'
import { selectStoredSession } from 'src/app/allSessions'
import { appApi, useUpdateSessionMutation } from 'src/app/appApiQueries'
import FullScreenSpinner from '../FullScreenSpinner'
import { selectPreviousPage, setCurrentPage } from '../../app/page'

type HostInactiveSessionProps = {
  id: string
  groupId: string
}

const HostInactiveSession: FC<HostInactiveSessionProps> = ({ groupId, id }) => {
  const dispatch = useAppDispatch()
  const previousPage = useAppSelector(selectPreviousPage)
  const [updateSession, { isLoading: isUpdateLoading }] =
    useUpdateSessionMutation()
  const { isLoading } =
    appApi.endpoints.getAllSessions.useQueryState('AllSessions')
  // we must memoize re-selectors if they will appear in different component instances
  const selectStoredSessionMemo = useMemo(() => selectStoredSession, [])
  const session = useAppSelector((state) =>
    selectStoredSessionMemo(state, { groupId, id })
  )
  const { name, participants, dateCreated } = session
  // if undefined will fallback to todays date
  const date = useCallback(() => formatDate(dateCreated), [dateCreated])
  const { isLoading: csvLoading, onExportToCSV } = useCreateCsv({
    participantArr: Object.values(participants),
    name,
  })

  const onDeleteSession = useCallback(() => {
    dispatch(setType({ type: 'sessionDelete', props: { id, groupId } }))
  }, [dispatch, id, groupId])

  const onGroupChange = (newGroupId: string) => {
    // history props also need to change, we will be on the new page by the time the data is ready from allSessions
    dispatch(
      setCurrentPage({ page: 'Session', props: { groupId: newGroupId, id } })
    )
    if (session) {
      updateSession({
        session: { ...session, groupId: newGroupId },
        oldGroupId: groupId,
      })
        .unwrap()
        // this will refetch all sessions
        .then(() => dispatch(appApi.util.invalidateTags(['AllSessions'])))
    }
  }

  return isLoading || isUpdateLoading || !name ? (
    <FullScreenSpinner styles="h-[calc(100vh-180px)] sm:h-screen" />
  ) : (
    <>
      <div className="flex justify-between pb-4">
        <BackButton page={previousPage} />
        <p className="text-grey text-sm whitespace-nowrap">{date()}</p>
      </div>
      <h1 className="font-bold text-base mb-4">{name}</h1>
      <TagDropdown
        setSelectedOption={onGroupChange}
        selectedValue={groupId}
        popoutWrapperStyles="mb-4"
      />

      {participants && (
        <SessionParticipants isHost={false} participants={participants} />
      )}

      <div className="inline-flex gap-2 justify-end fixed w-full right-0 bottom-0 bg-white p-4 sm:w-mainView">
        <EllipsesDropdown id="session-footer" placement="top-end">
          <button className="text-left" onClick={onDeleteSession}>
            Delete Session
          </button>
        </EllipsesDropdown>
        <CSVButton
          isLoading={csvLoading}
          onExportToCSV={onExportToCSV}
          disabled={Object.values(participants).length <= 0}
        />
      </div>
    </>
  )
}

export default HostInactiveSession
